/* eslint-disable max-lines */
import React from 'react';
import { Prompt } from 'react-router';
import _ from 'lodash';
import { styled } from '@this/constants/themes';

import type NonOrderItem from '@this/domain/non_order_item2';
import type TransportElement from '@this/domain/transport_element2';
import HotelElement from '@this/domain/hotel_element2';
import SimpleLoading from '@this/components/shared/simple_loading/simple_loading';
import StepBar from '@this/components/step_bar';

import Transit from '@this/domain/transit/transit2';
import Flight from '@this/domain/flight/flight2';
import Hotel from '@this/domain/hotel/hotel2';

import type UserJson from '@this/domain/user/user_json';
import User2, { convertUserResponseToArgs, type UserResponseArgs } from '@this/domain/user/user2';
import type ReserveInfo2 from '@this/domain/reserve_info2';
import type ReservingTripItem2 from '@this/src/domain/trip/reserving_trip_item2';
import Trip, { convertTripResponseToArgs, type TripResponseArgs } from '@this/domain/trip/trip2';

import type { RouteComponentProps } from 'react-router-dom';
import type { WorkflowStyle } from '@this/domain/workflow_style';
import type { SearchType } from '@this/domain/search_query2';
import type { ExpensesAccountTypeJson } from '@this/domain/expenses/expenses_account_type';
import { Fetcher, HTTPError, objectToFormData } from '@this/src/util';
import { reportError } from '@this/lib/bugsnag';
import type TripApproveItemJson from '@this/src/domain/trip_approve_item/trip_approve_item_json';
import Tooltip from '@this/shared/tooltip/tooltip';
import ReserveConfirmStore, {
  convertReserveConfirmStoreResponseToArgs,
  type ReserveConfirmStoreResponseArgs
} from '@this/src/domain/reserve_confirm_store';
import type ReservingTrip2 from '@this/domain/trip/reserving_trip2';
import type TravelerInformation from '@this/domain/traveler_information';
import { type TravelerInformationResponseArgs, buildTravelerParams } from '@this/domain/traveler_information';
import BufferAmountsSection from '@this/components/reserve_trip/reserve_confirm/outline/buffer_amounts_section';
import ReserveConfirmOutlineItemTemplate from '../../../reserve_trip/reserve_confirm/outline_item/outline_item.template';
import ReserveConfirmRoutingsTemplate from '../../../reserve_trip/reserve_confirm/routings/routings.template';
import { ConfirmWrapHeader } from '../../../reserve_trip/reserve_confirm/reserve_confirm_pc.template';
import { Wrap, BodyWrap, Body, LoadingWrap } from '../trips';

const TRIPS_CONFIRM_DATA_KEY = 'tripsConfirmData';
const SESSION_EXPIRED_ERROR_MESSAGE =
  'セッションが切れました。もう一度入力画面から始めてください。旅程情報登録画面に戻ります。';
const REDIRECT_DELAY_MS = 2000;

interface Props extends RouteComponentProps<Record<string, string | undefined>, Record<string, any>, any> {
  serviceId: number;
}

interface State {
  reserveInfo: ReserveInfo2 | null;
  trip: Trip | null;
  user: User2 | null;
  travelers: TravelerInformation[];
  searchQueryId: number | null;
  loading: boolean;
  steps: string[];
  stepIndex: number;
  shouldBlockNavigation: boolean;
  submitErrors: string[];
  error: string | null;
  termsAgree: boolean;
  useSmartSearch: boolean;
  reservingTripItems2: ReservingTripItem2[];
  reservingTrip2: ReservingTrip2 | null;
  reserveConfirmStore: ReserveConfirmStore | null;
}

interface TripsConfirmResponse {
  trip: TripResponseArgs;
  user: UserJson;
  travelers?: TravelerInformationResponseArgs[];
  items: {
    element_id: string;
    element_raw: never;
    element_type: 'transport' | 'hotel' | 'flight';
    foreign_exist: string; // "false" で返ってくる;
    changeable_air: string; // "true" で返ってくる;
    index: string; // "0" で返ってくる
    domestic_air_price_index: number;
  }[];
  approvers: never;
  type: never;
  package_type: never;
  package_search_condition: never;
  foreign_exist: never;
  domestic_exist: never;
  shinkansen_address: never;
  shinkansen_addressee: never;
  shinkansen_address_type: never;
  shinkansen_fee?: never;
  domestic_air_fee: never;
  foreign_air_fee: never;
  use_bulk_ticket: boolean;
  use_kyuusyuu_ticket: boolean;
  show_ex: boolean;
  has_members: boolean;
  organization_bases: never[];
  exic_cards: never[];
  departments: never[];
  charging_department_shares: never;
  postcode: never;
  cards: never[];
  car_type: [string, string][];
  rentalcar_available: boolean;
  rental_cars: never[];
  rental_car_limit_type: never;
  is_smoke: [string, string][];
  projects: never;
  project_shares: never;
  order_item_mapping_args: never[];
  outword: never;
  homeword: never;
  hotel?: { bed_types: { id: number; description: string }[] };
  flight: never;
  margin_types: never;
  show_fee: never;
  draft_trip: { purpose: never } | null;
  all_travelers_notification: never;
  notified_users: UserJson[];
  token_host: string;
  shop_id: string;
  consignment_sale: never;
  login_user_id: number;
  project_share_availability: boolean;
  prefecture: never[];
  internal_number: string;
  nationalities: { id: number; name: string }[];
  use_default_workflow: boolean;
  workflow_style: WorkflowStyle;
  use_smart_search: boolean;
  trip_duplications: never[];
  trip_approve_item: { json: string };
  expenses_account_type_available: boolean;
  expenses_account_types: ExpensesAccountTypeJson[];
  payment_gateway_members: never[] | undefined;
  traveler_candidates: UserJson[];
  use_charging_department_workflow: boolean;
  concur_expense_enabled: boolean;
  concur_connected: boolean;
}

interface SearchQueryResponse {
  queries: { peoplenum: number; roomnum: number; stay_days: number; search_type: SearchType } | null;
  search_query_id: never;
}

class TripsConfirm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      reserveInfo: null,
      trip: null,
      user: null,
      travelers: [],
      searchQueryId: null,
      loading: true,
      steps: ['Step 1. 入力', 'Step 2. 確認', 'Step 3. 完了'],
      stepIndex: 0,
      shouldBlockNavigation: true,
      submitErrors: [],
      error: null,
      termsAgree: false,
      useSmartSearch: false,
      reservingTripItems2: [],
      reservingTrip2: null,
      reserveConfirmStore: null
    };
  }

  componentDidMount() {
    let stateData = this.props.location.state;

    if (!stateData) {
      const stored = sessionStorage.getItem(TRIPS_CONFIRM_DATA_KEY);
      if (!stored) {
        this.setState({
          loading: false,
          error: SESSION_EXPIRED_ERROR_MESSAGE,
          shouldBlockNavigation: false
        });
        setTimeout(() => {
          this.props.history.push('/trips/new');
        }, REDIRECT_DELAY_MS);
        return;
      }

      try {
        stateData = JSON.parse(stored);
      } catch (e) {
        sessionStorage.removeItem(TRIPS_CONFIRM_DATA_KEY);
        this.setState({
          loading: false,
          error: SESSION_EXPIRED_ERROR_MESSAGE,
          shouldBlockNavigation: false
        });
        setTimeout(() => {
          this.props.history.push('/trips/new');
        }, REDIRECT_DELAY_MS);
        return;
      }
    }

    const storedSearchQueryId = sessionStorage.getItem('searchQueryId');
    Fetcher.post<SearchQueryResponse>('/search_queries', {
      query: stateData.query,
      search_result: stateData?.params?.search_result,
      search_query_id: storedSearchQueryId
    }).then(
      queryResult => {
        if (queryResult.search_query_id) {
          sessionStorage.setItem('searchQueryId', queryResult.search_query_id);
          this.setState({ searchQueryId: queryResult.search_query_id });
        }

        Fetcher.post<TripsConfirmResponse>(`${location.href}.json`, objectToFormData(stateData.params)).then(
          async result => {
            const trip = new Trip(convertTripResponseToArgs(result.trip, result.show_fee));
            if (!trip.draft) {
              alert('この旅程は既に申請済みです。');
              this.props.history.push('/trips');
            }
            const approvers = _.sortBy(result.approvers, 'approve_stage');
            if (result.user.department != null) {
              result.user.department.approvers = approvers;
            }
            const user2 = new User2(
              convertUserResponseToArgs({
                ...result.user,
                nationality_id: result.user.nationality_id ?? undefined
              } as unknown as UserResponseArgs)
            );
            const reserveConfirmStore = new ReserveConfirmStore(
              convertReserveConfirmStoreResponseToArgs(
                {
                  ...(result as unknown as ReserveConfirmStoreResponseArgs),
                  user: {
                    ...(result.user as unknown as UserResponseArgs),
                    nationality_id: result.user.nationality_id ?? undefined
                  },
                  travelers: result.travelers ?? result.trip?.traveler_informations ?? []
                },
                { isSimpleRequest: false, fromTripForm: true }
              )
            );
            const reserveInfo = reserveConfirmStore.reserveInfo;
            const reservingTrip2 = reserveInfo.reservingTrip;
            const reservingTripItems2 = reservingTrip2.items;
            const travelers2 = reservingTrip2.travelers;
            if (reservingTrip2.isShinkansenInfoRequired && !reserveInfo.preferredTicketingMethod) {
              const organization = reserveInfo.user.organization;
              reserveInfo.preferredTicketingMethod =
                organization?.usePostal && !organization?.useQr
                  ? 'postal'
                  : organization?.useQr || organization?.usePostal
                  ? 'qr'
                  : null;
            }
            travelers2.forEach(traveler => {
              if (!traveler.type) {
                traveler.type = 'self';
              }
              if (traveler.type === 'self' && !traveler.id && !traveler.userId) {
                traveler.assignFromUser(user2);
              }
              if (traveler.type === 'self' && !traveler.userId && user2.id) {
                traveler.userId = user2.id;
              }
              if (!traveler.id && traveler.userId) {
                traveler.id = traveler.userId;
              }
              if (!traveler.name && traveler.userName) {
                traveler.name = traveler.userName;
              }
              if (!traveler.name && (user2.lastName || user2.firstName)) {
                traveler.name = `${user2.lastName || ''}${user2.firstName || ''}`;
              }
            });
            const tripApproveItems = result.trip.trip_approve_item?.json
              ? (JSON.parse(result.trip.trip_approve_item.json) as TripApproveItemJson[])
              : null;
            if (tripApproveItems) {
              tripApproveItems.forEach(item => {
                if (item.value != null) {
                  reserveInfo.approveItemValues.set(item.id, item.value);
                }
                if (item.valueCode != null) {
                  reserveInfo.approveItemValueCodes.set(item.id, item.valueCode);
                }
              });
              reserveInfo.fetchAttachedApproveItemFiles(trip.id, tripApproveItems);
            }

            await reserveInfo.fetchApproveItems('before_business_trip');
            this.setState({
              reserveInfo,
              trip,
              user: user2,
              travelers: travelers2,
              searchQueryId: queryResult.search_query_id,
              loading: false,
              useSmartSearch: result.use_smart_search,
              reservingTripItems2,
              reservingTrip2,
              reserveConfirmStore
            });
          },
          () => {
            this.setState({
              error: '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
            });
          }
        );
      },
      () => {
        this.setState({
          error: '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
        });
      }
    );
  }

  componentDidUpdate() {
    // 未保存離脱の警告をPromptで実装しているが、再読み込みとタブの終了に対応していないため
    if (this.state.shouldBlockNavigation) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  }

  componentWillUnmount() {
    sessionStorage.removeItem('searchQueryId');
  }

  getApplicant() {
    const applicant = _.first(this.state.travelers);
    if (applicant?.id) {
      return applicant;
    }
    return this.state.user;
  }

  itemsTotalPrice() {
    if (!this.state.reserveInfo) return 0;

    const prices = _.map(
      utils.dig(this.state.reserveInfo.reservingTrip, 'items') || [],
      item => (item && item.totalPrice) || 0
    );
    return _.sum(prices);
  }

  itemsPricePerType(type: 'shinkansen' | 'air' | 'rental_car' | 'hotel') {
    if (!this.state.reserveInfo) return 0;

    const elements = _.map(
      utils.dig(this.state.reserveInfo.reservingTrip, 'items') || [],
      item => item && item.element
    );
    const filteredElements = (() => {
      switch (type) {
        case 'shinkansen':
          return _.filter(elements, e => e instanceof Transit && e.shinkansen);
        case 'air':
          return _.filter(elements, e => (e instanceof Transit && e.air) || e instanceof Flight);
        case 'hotel':
          return _.filter(elements, e => e instanceof Hotel);
        default:
          return [];
      }
    })();
    if (filteredElements.length === 0) {
      return 0;
    }
    const prices = _.map(filteredElements, e => (e && e.totalPrice) || 0);
    return _.sum(prices);
  }

  isCustomerInfoRequired = () => !!this.state.user && !!this.state.user.organization;

  handleBackToStep1Click = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    this.setState(
      {
        shouldBlockNavigation: false
      },
      () => {
        this.props.history.push(`${location.pathname}#`);
        this.setState({
          shouldBlockNavigation: true,
          stepIndex: 0
        });
      }
    );
  };

  handleValidationSuccess = () => {
    this.setState(
      {
        shouldBlockNavigation: false
      },
      () => {
        this.props.history.push(`${location.pathname}#step2`);
        this.setState({
          shouldBlockNavigation: true,
          stepIndex: 1
        });
      }
    );
    return utils.scrollToTop();
  };

  handleSaveDraft = () => {
    this.handleSubmit(true);
  };

  handleConfirm = () => {
    this.handleSubmit(false);
  };

  handleSubmit(asDraft?: boolean) {
    const trip = this.state.trip;
    if (!trip) return;
    const params = {
      as_draft: asDraft,
      payment_type: utils.dig(this.state.reserveInfo, 'paymentMethod') || 'billing',
      payment_transaction: utils.dig(this.state.reserveInfo, 'paymentTransaction'),
      reserve_info: this.state.reserveInfo ? this.state.reserveInfo.submitParams : null,
      user_id: trip.userId,
      travelers: buildTravelerParams(
        this.state.travelers,
        this.state.reserveInfo?.reservingTrip?.isForeignFlightCustomerInfoRequired ?? false
      ),
      title: trip.title,
      departure_time: trip.startTime!.format('YYYY-MM-DD HH:mm ZZ'),
      return_time: trip.endTime!.format('YYYY-MM-DD HH:mm ZZ'),
      applicant_id: utils.dig(this.getApplicant(), 'id'),
      search_query_id: this.state.searchQueryId,
      non_order_items: _.compact(_.map(trip.nonOrderItems, item => item.updateParams))
    };

    let path = '/trips';
    let method: 'POST' | 'PUT' = 'POST';
    if (trip.id) {
      path += `/${trip.id}`;
      method = 'PUT';
    }

    Fetcher.request({ url: `${path}.json`, method, data: objectToFormData(params) })
      .then(() => {
        sessionStorage.removeItem(TRIPS_CONFIRM_DATA_KEY);

        this.setState(
          {
            shouldBlockNavigation: false
          },
          () => {
            location.href = '/trips';
          }
        );
      })
      .catch(e => {
        let errors;
        if (e instanceof HTTPError && e.response?.data.errors) {
          const serverErrors = e.response?.data.errors;
          errors = Array.isArray(serverErrors) ? serverErrors : [serverErrors];
        } else {
          errors = ['通信環境が不安定です。\n時間をおいてもう一度お試しください。'];
        }
        this.setState({
          submitErrors: errors
        });
      });
  }

  clickBackLink = () => {
    sessionStorage.removeItem(TRIPS_CONFIRM_DATA_KEY);
    const trip = this.state.trip;

    this.setState(
      {
        shouldBlockNavigation: false
      },
      () => {
        this.props.history.push({
          pathname: trip?.id ? `/trips/${trip.id}/edit` : '/trips/new',
          state: this.backState()
        });
      }
    );
  };

  backState() {
    const trip = this.state.trip;
    if (!trip) return null;
    return {
      title: trip.title,
      travelers: buildTravelerParams(
        this.state.travelers,
        this.state.reserveInfo?.reservingTrip?.isForeignFlightCustomerInfoRequired ?? false
      ),
      outdate: trip.startTime!.format('YYYY-MM-DD HH:mm'),
      homedate: trip.endTime!.format('YYYY-MM-DD HH:mm'),
      purpose: utils.dig(this.state.reserveInfo, 'purpose'),
      final_destination: utils.dig(this.state.reserveInfo, 'finalDestination'),
      non_order_items: _.compact(_.map(trip.nonOrderItems, item => item.updateParams)),
      search_query_id: this.state.searchQueryId
    };
  }

  handleAgreementTermsCheck = () => {
    this.setState({
      termsAgree: !this.state.termsAgree
    });
  };

  render() {
    const {
      reserveInfo,
      trip,
      loading,
      steps,
      stepIndex,
      error,
      submitErrors,
      shouldBlockNavigation,
      useSmartSearch,
      reservingTrip2,
      reserveConfirmStore
    } = this.state;

    if (!trip && !loading && !error) {
      return null;
    }
    try {
      return (
        <Wrap data-testid="trips-confirm-root">
          <Prompt
            when={shouldBlockNavigation}
            message="保存されていないデータが失われます。本当によろしいですか？"
          />
          <BodyWrap>
            <div>
              <StepBarWrap>
                <StepBar steps={steps} index={stepIndex} />
              </StepBarWrap>
              <ConfirmBody>
                <BodyIn>
                  {loading ? (
                    <LoadingWrap>
                      <SimpleLoading />
                    </LoadingWrap>
                  ) : error ? (
                    <LoadingWrap>
                      <Error>{error}</Error>
                    </LoadingWrap>
                  ) : trip ? (
                    <>
                      <BodyHeader>
                        <Title>
                          旅程番号：<span data-wovn-ignore>{trip.id || '未発行'}</span>
                          <br />
                          旅程名：
                          <span data-testid="trips-confirm-trip-title" data-wovn-ignore>
                            {trip.title}
                          </span>
                          <br />
                          出張日時：<span data-wovn-ignore>{trip.dateRange}</span>
                        </Title>
                      </BodyHeader>
                      <ItemsBlock>
                        <ItemsBlockHeader>
                          <BlockTitle>【予約内容内訳】</BlockTitle>
                          <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 'auto' }}>
                            <ItemsTotalPrice>
                              合計金額：
                              <Price data-wovn-ignore>
                                {utils.formatPrice(this.itemsTotalPrice() + trip.nonOrderItemsTotalPrice)}
                              </Price>
                            </ItemsTotalPrice>
                            {reservingTrip2 && reservingTrip2.itemsTotalBufferAmount > 0 && (
                              <>
                                <div
                                  style={{
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span style={{ marginRight: '5px' }}>値上がり許容幅込の申請金額</span>
                                    <Tooltip type="info">
                                      <div className="select__tooltip-format">
                                        承認後、実際に商品を手配する前に値上がりが発生した場合、いくらまで値上がりを許容して手配するかを管理画面で設定いただいています。
                                      </div>
                                    </Tooltip>
                                    <span>：</span>
                                  </div>
                                  <div>{utils.digits(reservingTrip2.totalPriceWithBuffer)}円</div>
                                </div>
                                <div style={{ fontSize: '12px', marginTop: '12px' }}>
                                  <BufferAmountsSection reservingTrip={reservingTrip2} />
                                </div>
                              </>
                            )}
                          </div>
                        </ItemsBlockHeader>
                        {useSmartSearch && (
                          <ItemsSection>
                            <ItemsTitle>□■システム手配分■□</ItemsTitle>
                            <ItemsContent>
                              <ItemsCost>
                                <ItemsCostDetail>
                                  <ItemsCostTitle>【交通費】</ItemsCostTitle>
                                  <ItemsCostTable>
                                    <tbody>
                                      <ItemsCostTableTr>
                                        <ItemsCostTableTh>新幹線</ItemsCostTableTh>
                                        <ItemsCostTableSeparator>：</ItemsCostTableSeparator>
                                        <ItemsCostTableTd data-wovn-ignore>
                                          {utils.formatPrice(this.itemsPricePerType('shinkansen'))}
                                        </ItemsCostTableTd>
                                      </ItemsCostTableTr>
                                      <ItemsCostTableTr>
                                        <ItemsCostTableTh>航空機</ItemsCostTableTh>
                                        <ItemsCostTableSeparator>：</ItemsCostTableSeparator>
                                        <ItemsCostTableTd data-wovn-ignore>
                                          {utils.formatPrice(this.itemsPricePerType('air'))}
                                        </ItemsCostTableTd>
                                      </ItemsCostTableTr>
                                      <ItemsCostTableTr>
                                        <ItemsCostTableTh>レンタカー</ItemsCostTableTh>
                                        <ItemsCostTableSeparator>：</ItemsCostTableSeparator>
                                        <ItemsCostTableTd data-wovn-ignore>
                                          {utils.formatPrice(this.itemsPricePerType('rental_car'))}
                                        </ItemsCostTableTd>
                                      </ItemsCostTableTr>
                                    </tbody>
                                  </ItemsCostTable>
                                  <ItemsCostTitle>【宿泊費】</ItemsCostTitle>
                                  <ItemsCostTable>
                                    <tbody>
                                      <ItemsCostTableTr>
                                        <ItemsCostTableTh>宿泊費</ItemsCostTableTh>
                                        <ItemsCostTableSeparator>：</ItemsCostTableSeparator>
                                        <ItemsCostTableTd data-wovn-ignore>
                                          {utils.formatPrice(this.itemsPricePerType('hotel'))}
                                        </ItemsCostTableTd>
                                      </ItemsCostTableTr>
                                    </tbody>
                                  </ItemsCostTable>
                                </ItemsCostDetail>
                                <ItemsCostSum>
                                  ①合計：
                                  <span data-wovn-ignore>{utils.formatPrice(this.itemsTotalPrice())}</span>
                                </ItemsCostSum>
                              </ItemsCost>
                              <ItemsDetail>
                                <ItemsDetailTitle>旅程明細①</ItemsDetailTitle>
                                {this.state.reservingTripItems2.map(
                                  (item: ReservingTripItem2 | undefined, i: number) =>
                                    item && (
                                      <ReserveConfirmOutlineItemTemplate
                                        isPackage={false}
                                        key={i}
                                        item={item}
                                        domesticAirPriceIndex={item.domesticAirPriceIndex}
                                      />
                                    )
                                )}
                                <ItemsDetailSubTitle>
                                  【合計】
                                  <Price>{utils.formatPrice(this.itemsTotalPrice())}</Price>
                                </ItemsDetailSubTitle>
                              </ItemsDetail>
                            </ItemsContent>
                          </ItemsSection>
                        )}
                        <ItemsSection>
                          <ItemsTitle>□■個人手配分■□</ItemsTitle>
                          <ItemsContent>
                            <ItemsCost>
                              <ItemsCostDetail>
                                <ItemsCostTitle>【交通費】</ItemsCostTitle>
                                <ItemsCostTable>
                                  <tbody>
                                    <ItemsCostTableTr>
                                      <ItemsCostTableTh>新幹線</ItemsCostTableTh>
                                      <ItemsCostTableSeparator>：</ItemsCostTableSeparator>
                                      <ItemsCostTableTd data-wovn-ignore>
                                        {utils.formatPrice(trip.nonOrderItemsTotalPriceByTypes(['shinkansen']))}
                                      </ItemsCostTableTd>
                                    </ItemsCostTableTr>
                                    <ItemsCostTableTr>
                                      <ItemsCostTableTh>航空機</ItemsCostTableTh>
                                      <ItemsCostTableSeparator>：</ItemsCostTableSeparator>
                                      <ItemsCostTableTd data-wovn-ignore>
                                        {utils.formatPrice(
                                          trip.nonOrderItemsTotalPriceByTypes(['domestic_air', 'foreign_air'])
                                        )}
                                      </ItemsCostTableTd>
                                    </ItemsCostTableTr>
                                    <ItemsCostTableTr>
                                      <ItemsCostTableTh>レンタカー</ItemsCostTableTh>
                                      <ItemsCostTableSeparator>：</ItemsCostTableSeparator>
                                      <ItemsCostTableTd data-wovn-ignore>
                                        {utils.formatPrice(trip.nonOrderItemsTotalPriceByTypes(['rental_car']))}
                                      </ItemsCostTableTd>
                                    </ItemsCostTableTr>
                                    <ItemsCostTableTr>
                                      <ItemsCostTableTh>その他</ItemsCostTableTh>
                                      <ItemsCostTableSeparator>：</ItemsCostTableSeparator>
                                      <ItemsCostTableTd data-wovn-ignore>
                                        {utils.formatPrice(
                                          trip.nonOrderItemsTotalPriceByTypes(['railway_ticket', 'bus', 'taxi'])
                                        )}
                                      </ItemsCostTableTd>
                                    </ItemsCostTableTr>
                                  </tbody>
                                </ItemsCostTable>
                                <ItemsCostTitle>【宿泊費】</ItemsCostTitle>
                                <ItemsCostTable>
                                  <tbody>
                                    <ItemsCostTableTr>
                                      <ItemsCostTableTh>宿泊費</ItemsCostTableTh>
                                      <ItemsCostTableSeparator>：</ItemsCostTableSeparator>
                                      <ItemsCostTableTd data-wovn-ignore>
                                        {utils.formatPrice(trip.nonOrderItemsTotalPriceByTypes(['hotel']))}
                                      </ItemsCostTableTd>
                                    </ItemsCostTableTr>
                                  </tbody>
                                </ItemsCostTable>
                                <ItemsCostTitle>【その他】</ItemsCostTitle>
                                <ItemsCostTable>
                                  <tbody>
                                    <ItemsCostTableTr>
                                      <ItemsCostTableTh>Wifi</ItemsCostTableTh>
                                      <ItemsCostTableSeparator>：</ItemsCostTableSeparator>
                                      <ItemsCostTableTd data-wovn-ignore>
                                        {utils.formatPrice(trip.nonOrderItemsTotalPriceByTypes(['wifi']))}
                                      </ItemsCostTableTd>
                                    </ItemsCostTableTr>
                                    <ItemsCostTableTr>
                                      <ItemsCostTableTh>会議室</ItemsCostTableTh>
                                      <ItemsCostTableSeparator>：</ItemsCostTableSeparator>
                                      <ItemsCostTableTd data-wovn-ignore>
                                        {utils.formatPrice(trip.nonOrderItemsTotalPriceByTypes(['room']))}
                                      </ItemsCostTableTd>
                                    </ItemsCostTableTr>
                                  </tbody>
                                </ItemsCostTable>
                              </ItemsCostDetail>
                              <ItemsCostSum>
                                ②合計：
                                <span data-wovn-ignore>{utils.formatPrice(trip.nonOrderItemsTotalPrice)}</span>
                              </ItemsCostSum>
                            </ItemsCost>
                            <ItemsDetail>
                              <ItemsDetailTitle>旅程明細②</ItemsDetailTitle>
                              {_.sortBy(trip.nonOrderItems, item =>
                                item.startDateTime ? item.startDateTime.valueOf() : 0
                              ).map((item: NonOrderItem, i: number) => (
                                <ItemsDetailItem key={i}>
                                  <ItemsDetailSubTitle>
                                    {item.elements[0].type === 'hotel' ? '【宿泊】' : '【経路】'}
                                  </ItemsDetailSubTitle>
                                  {item.elements.map((element: TransportElement | HotelElement, j: number) => (
                                    <ItemsDetailElement key={j}>
                                      {element instanceof HotelElement ? (
                                        <>
                                          <ItemsDetailText data-wovn-ignore>{element.name}</ItemsDetailText>
                                          <ItemsDetailText data-wovn-ignore>
                                            <p>{element.summary}</p>
                                            {element.address && element.address.length !== 0 && (
                                              <p>{element.address}</p>
                                            )}
                                            <p>{`${item.paymentTypeStr}：${utils.formatPrice(
                                              item.totalPrice
                                            )}`}</p>
                                          </ItemsDetailText>
                                        </>
                                      ) : (
                                        <>
                                          <div data-wovn-ignore>
                                            {`${element.boardingStation} ${element.from.time
                                              .tz(element.from.timeZone)
                                              .format('MM/DD HH:mm')} 発 -> ${
                                              element.arrivalStation
                                            } ${element.to.time.tz(element.to.timeZone).format('MM/DD HH:mm')} 着`}
                                          </div>
                                          <ItemsDetailText data-wovn-ignore>{element.title}</ItemsDetailText>
                                          {element.name && (
                                            <ItemsDetailText data-wovn-ignore>{element.name}</ItemsDetailText>
                                          )}
                                          <ItemsDetailText data-wovn-ignore>
                                            {`
                                              出発${
                                                ['railway_ticket', 'shinkansen'].indexOf(element.transportType) >
                                                -1
                                                  ? '駅'
                                                  : '地'
                                              }:
                                              ${element.boardingStation}
                                              到着${
                                                ['railway_ticket', 'shinkansen'].indexOf(element.transportType) >
                                                -1
                                                  ? '駅'
                                                  : '地'
                                              }:
                                              ${element.arrivalStation}
                                            `}
                                          </ItemsDetailText>
                                        </>
                                      )}
                                    </ItemsDetailElement>
                                  ))}
                                </ItemsDetailItem>
                              ))}
                              <ItemsDetailSubTitle>
                                【合計】
                                <Price>{utils.formatPrice(trip.nonOrderItemsTotalPrice)}</Price>
                              </ItemsDetailSubTitle>
                            </ItemsDetail>
                          </ItemsContent>
                        </ItemsSection>
                      </ItemsBlock>
                      {reserveInfo && reserveConfirmStore && shouldBlockNavigation && (
                        // 上のshouldBlockNavigationはステップを行き来するときに一時非表示にすることで中身の更新を促すため
                        <ReserveConfirmRoutingsTemplate
                          store={reserveConfirmStore}
                          handleBackToSelectClick={this.clickBackLink}
                          handleSaveDraft={this.handleSaveDraft}
                          handlePaymentSubmit={this.handleConfirm}
                          isOnTripsConfirm
                          handleValidationSuccess={this.handleValidationSuccess}
                          handleBackToStep1Click={this.handleBackToStep1Click}
                        />
                      )}
                    </>
                  ) : null}
                  {submitErrors && submitErrors.length > 0 && (
                    <>
                      {submitErrors.map((error, i) => (
                        <Error key={i}>{error}</Error>
                      ))}
                    </>
                  )}
                </BodyIn>
              </ConfirmBody>
            </div>
          </BodyWrap>
        </Wrap>
      );
    } catch (e) {
      reportError(e);
      return null;
    }
  }
}

const StepBarWrap = styled(ConfirmWrapHeader)`
  width: 100%;
  max-width: 1150px;
  margin: 0 auto;
  padding: 20px 0;
`;

const ConfirmBody = styled(Body)``;

const BodyIn = styled.div`
  padding: 40px 20px;
`;

const Error = styled.div`
  color: ${props => props.theme.redColor};
`;

const BodyHeader = styled.div``;

const Title = styled.div`
  margin-bottom: 20px;
  font-weight: bold;
`;

const BlockTitle = styled.div`
  font-weight: bold;
`;

const ItemsBlock = styled.div`
  margin-bottom: 50px;
  padding: 20px;
  border: solid 1px ${props => props.theme.grayBorderColor};
`;

const ItemsBlockHeader = styled.div`
  display: flex;
`;

const ItemsTotalPrice = styled.div`
  margin-left: auto;
  font-size: 16px;
  font-weight: bold;
`;

const ItemsSection = styled.div`
  padding-top: 20px;
`;

const ItemsTitle = styled.div`
  margin-bottom: 10px;
`;

const ItemsContent = styled.div`
  display: flex;

  @media screen and (max-width: 767px) {
    flex-direction: column;
  }
`;

const ItemsCost = styled.div`
  width: 60%;
  margin-right: 40px;
  font-size: 13px;

  @media screen and (max-width: 767px) {
    width: 100%;
    margin-right: 0;
    margin-bottom: 20px;
  }
`;

const ItemsCostDetail = styled.div`
  margin-bottom: 10px;
  padding: 10px;
  border: solid 1px ${props => props.theme.grayBorderColor};
`;

const ItemsCostSum = styled.div`
  padding: 10px;
  border: solid 1px ${props => props.theme.grayBorderColor};
  border-radius: 5px;
  font-size: 14px;
  text-align: center;
`;

const ItemsCostTitle = styled.div`
  font-weight: bold;
`;

const ItemsCostTable = styled.table`
  width: auto;
  margin: 0 0 10px;
`;

const ItemsCostTableTr = styled.tr``;

const ItemsCostTableTh = styled.th`
  min-width: 100px;
  padding: 0 15px;
  border: 0;
  font-weight: normal;
`;

const ItemsCostTableSeparator = styled.td`
  padding: 0;
  border: 0;
`;

const ItemsCostTableTd = styled.td`
  width: 100px;
  padding: 0 15px;
  border: 0;
  text-align: right;
`;

const ItemsDetail = styled.div`
  width: 40%;
  padding: 20px;
  background: ${props => props.theme.grayBgColor};
  font-size: 13px;

  @media screen and (max-width: 767px) {
    width: 100%;
  }
`;

const ItemsDetailTitle = styled.div`
  margin-bottom: 15px;
  font-weight: bold;
`;

const ItemsDetailSubTitle = styled.div`
  font-weight: bold;
`;

const ItemsDetailItem = styled.div`
  margin-bottom: 15px;
  padding-left: 10px;
`;

const ItemsDetailElement = styled.div``;

const ItemsDetailText = styled.div``;

const Price = styled.span`
  color: ${props => props.theme.redColor};
  font-weight: bold;
`;

export default TripsConfirm;
